import {
  TRESPONSE_LLBoardListing,
  TLLEntityData,
  TLLEntityGroupData,
  TLLElementData,
  TCustomView,
  TDate,
  TLlMachineAvailabilityDayWiseKPIs,
  TBoardViewMode,
  TLLProductionStatus,
} from '@monorepo/types';
import { SVG_PATHS } from '../timeline.constants';
import { TIMELINE_MODEL } from '../timeline.model';
import { TRange, TTimelineModel, TRectDropElement, TEntityActions } from '../timeline.types';
import {
  getLeftNavBackgroundColour,
  getLlBorderColorPallet,
  getLlStripColorPallet,
  getLlTextColorPallet,
} from '../timeline.utils';

export const getThemeViewDisplayName = (key: TCustomView['viewMode']): string => {
  switch (key) {
    case 'BUYER':
      return 'Buyer';
    case 'PRODUCT_TYPE':
      return 'Product Type';
    case 'TYPE_OF_ORDER':
      return 'Type of Order';
    case 'DELIVERY_MONTH':
      return 'Delivery Month';
    case 'DEFAULT':
      return 'Order';
    default:
      return 'Order';
  }
};
const MACHINE_AVAILABILITY_LINE_ID_PREFIX = 'machine_availability_';

const PRODUCTION_STATUS_LABEL: Record<TLLProductionStatus, string> = {
  NOT_STARTED: 'Not Started',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
};

type TLlBoardLine = TTimelineModel<
  TLLEntityGroupData,
  TLLEntityData,
  TLLElementData
>['entities'][number]['entities'][number];

function getMachineAvailabilityElements(dayWiseKPIs: TLlMachineAvailabilityDayWiseKPIs): TLlBoardLine['elements'] {
  return Object.entries(dayWiseKPIs).map<TLlBoardLine['elements'][number]>(([key, value]) => ({
    type: 'STRIP',
    color: value.machineAvailability.color,
    iconList: [],
    id: `machine_availability_strip_${key}`,
    mask: false,
    name: `${value.machineAvailability.availablePercentage}%`,
    range: {
      startDate: `${key} 00:45:00` as TDate,
      endDate: `${key} 23:15:00` as TDate,
    },
    data: { type: 'MACHINE_AVAILABILITY_STRIP', elementData: value.machineAvailability, date: key as TDate },
    stackCount: 1,
    textList: [
      {
        color: '#000',
        value: `${Math.round(value.machineAvailability.availablePercentage)}%`,
        align: 'center',
        justify: 'center',
        fontSizeInPx: 12,
      },
    ],
  }));
}

export const LL_MODEL = {
  createModel: (
    llBoardResponse: TRESPONSE_LLBoardListing,
    validLineSubjects?: string[],
    highlightedStripId?: string,
  ): TLLTimelineModel => {
    const flattenFactory = llBoardResponse.llBoardDTO.llBoardByFactoryList;

    const timelineModel = TIMELINE_MODEL.createTimelineModel<TLLEntityGroupData, TLLEntityData, TLLElementData>(
      {
        ...llBoardResponse.llBoardDTO.boardView,
        ...(llBoardResponse.llBoardDTO.customView?.viewMode && {
          themeView: {
            key: llBoardResponse.llBoardDTO.customView.viewMode,
            displayName: getThemeViewDisplayName(llBoardResponse.llBoardDTO.customView.viewMode),
          },
        }),
      },
      flattenFactory,
      (factoryInfo: TLLEntityGroupData, factoryIndex: number) => {
        const machineAvailabilityLines: TLlBoardLine[] =
          Array<TBoardViewMode>('WEEKLY', 'DAILY').includes(llBoardResponse.llBoardDTO.boardView.boardViewType) &&
          factoryInfo.kpisInfo
            ? [
                {
                  id: `machine_availability_${factoryInfo.factoryId}`,
                  data: {
                    type: 'MACHINE_AVAILABILITY_LINE',
                    dayWiseMachineAvailability: factoryInfo.kpisInfo.dayWiseKPIs,
                  },
                  name: {
                    displayTxt: factoryInfo.factoryName ?? '',
                    isClickable: true,
                  },
                  metadata: {
                    title: 'Machine Capacity',
                    leftCount: 0,
                    rightCount: 0,
                    subTitle: '',
                  },
                  elements: getMachineAvailabilityElements(factoryInfo.kpisInfo.dayWiseKPIs),
                },
              ]
            : [];

        // entities for each factory
        const oplLines = factoryInfo.llBoardLines.map((v): TLlBoardLine => {
          // these elements are strips for each line of a factory
          const oplStrips = v.stripInfoList.map((s, index, strips): TLlBoardLine['elements'][number] => {
            const { color: textColor } = getLlTextColorPallet(s.stripTextColor);
            const { color: borderColor } = getLlBorderColorPallet(s.stripBorderColor);
            const { color: stripBgColor } = getLlStripColorPallet(s.stripColor);
            const endDate = s.adjustStripEndTimeToDayEndTime ? s.endTime.split(' ')[0] : s.endTime;
            const increaseHeight = index + 1 < strips.length && strips[index + 1].startTime < s.endTime;
            const isHighlighted = highlightedStripId === s.stripId;

            return {
              type: 'STRIP' as const,
              id: s.stripId,
              name: s.stripName,
              range: { startDate: s.startTime, endDate } as TRange,
              linkedToPrevious: s.isStripLinked,
              stackCount: 0 as const,
              heightInPercent: increaseHeight ? 130 : 100,
              color: stripBgColor,
              mask: false,
              data: {
                type: 'OPL_STRIP' as const,
                elementData: {
                  ...s,
                  lineSubject: v.lineSubject,
                  factoryId: factoryInfo.factoryId,
                },
              },
              textList: [
                {
                  value: s.orderQty?.orderQtyDisplay ? `${s.stripName} (${s.orderQty.orderQty})` : s.stripName,
                  color: textColor,
                },
              ],
              textBorderColor: borderColor,
              isStripHighlighted: isHighlighted,
              ...(s.note && {
                note: s.note,
              }),
              iconList: [
                ...(s.note
                  ? [
                      {
                        iconKey: 'NOTE' as const,
                        svgPath: SVG_PATHS.note,
                        svgColor: '#0F31A9',
                        background: '#C4D0FA',
                        clickAction: 'NOTE_CLICK' as const,
                      },
                    ]
                  : []),
                ...(s.llBoardStripMetadataDTO.lcapplied
                  ? [
                      {
                        iconKey: 'LC_APPLIED' as const,
                        svgPath: SVG_PATHS.lcApplied,
                        svgColor: '#FF04AA',
                        background: '#C4D0FA',
                      },
                    ]
                  : []),
                ...(s.llBoardStripMetadataDTO.isLoadDateBeforePCD
                  ? [
                      {
                        iconKey: 'LOAD_BEFORE_PCD' as const,
                        svgPath: SVG_PATHS.loadBeforePcd,
                        svgColor: '#B240FF',
                        fillRule: 'evenodd' as const,
                        background: 'none',
                      },
                    ]
                  : []),
                ...(s.isOPLClosed
                  ? [
                      {
                        iconKey: 'CLOSE_OPL' as const,
                        svgPath: SVG_PATHS.closeOpl,
                        svgColor: '#0D8525',
                        background: 'none',
                      },
                    ]
                  : []),
                ...(s.fabricInHousePercentage
                  ? [
                      {
                        iconKey: 'FABRIC_INHOUSE' as const,
                        svgPath: SVG_PATHS.fabricInHouse,
                        svgColor: '#FFFFFF',
                        text: `${s.fabricInHousePercentage}%`,
                        background:
                          s.fabricInHousePercentage <= 50
                            ? '#D32F2F'
                            : s.fabricInHousePercentage <= 75
                            ? '#FF9900'
                            : '#0D8525',
                      },
                    ]
                  : []),
                ...(s.productionStatus === 'IN_PROGRESS'
                  ? [
                      {
                        iconKey: 'PRODUCTION_IN_PROGRESS' as const,
                        svgPath: SVG_PATHS.productionInProgress,
                        svgColor: '#882617',
                        fillRule: 'evenodd' as const,
                        background: '#BADBF8',
                      },
                    ]
                  : []),
                ...(s.productionStatus === 'COMPLETED'
                  ? [
                      {
                        iconKey: 'PRODUCTION_COMPLETED' as const,
                        svgPath: SVG_PATHS.productionCompleted,
                        svgColor: '#0D8525',
                        fillRule: 'evenodd' as const,
                        background: '#BADBF8',
                      },
                    ]
                  : []),
              ],
              isStripOutdated: s.isStripOutdated,
              ...((s.loadedQty || s.fabricInHousePercentage !== undefined) && {
                additionalInfo: [
                  s.loadedQty &&
                    `Loaded Qty: ${s.loadedQty} </br> Produced Qty: ${s.producedQty ?? 0} </br> Production Progress: ${
                      s.loadedQty ? `${Math.min(s.productionProgress ?? 0, 100)}%` : 'N/A'
                    } </br> Status: ${
                      PRODUCTION_STATUS_LABEL[s.productionStatus] || PRODUCTION_STATUS_LABEL.NOT_STARTED
                    } </br> Bal to ex-fac: ${
                      s.llBoardStripMetadataDTO.daysFromStripEndToExFactory || '--'
                    } </br> SMV: ${s.smv}`,
                  s.fabricInHousePercentage !== undefined && `Fabric Inhouse: ${s.fabricInHousePercentage}%`,
                ]
                  .filter(Boolean)
                  .join(' </br> '),
              }),
            };
          });

          // this is at line level
          return {
            id: v.lineSubject,
            data: { type: 'OPL_LINE', ...v },
            name: {
              displayTxt: v.leftPanel.lineDisplayName,
              isClickable: true,
            },
            disabled: validLineSubjects && !validLineSubjects.includes(v.lineSubject),
            metadata: {
              title: v.leftPanel.factoryName,
              subTitle: v.leftPanel.floorDisplayName,
              leftCount: v.leftPanel.numOfStrips,
              rightCount: v.leftPanel.numOfDefaultOperators,
              ...(v.leftPanel.isPlannedOperatorCountIncreased && {
                icon: {
                  name: 'EXCEEDING_WORKFORCE' as const,
                  tooltip: 'Exceeding Default Operator Count',
                  svgPath: SVG_PATHS.exceedingWorkforce,
                  fill: '#DB3F27',
                  isClickable: true,
                },
              }),
            },
            elements: [
              ...oplStrips,
              ...v.lineMetadataDTO.workingHours.overtimeDays.map((t) => {
                const x = {
                  type: 'MESH' as const,
                  id: v.lineSubject,
                  name: 'Overtime',
                  range: {
                    startDate: t,
                    endDate: t,
                  } as TRange,
                  stackCount: 1 as const,
                  color: '#fc7c7c',
                  mask: true,
                };
                return x;
              }),
              ...v.lineMetadataDTO.workingHours.offstandardDays.map((t) => {
                const x = {
                  id: v.lineSubject,
                  type: 'MESH' as const,
                  name: 'Reduced working time',
                  range: {
                    startDate: t,
                    endDate: t,
                  } as TRange,
                  stackCount: 1 as const,
                  color: '#7e7cfc',
                  mask: true,
                };
                return x;
              }),
              ...v.lineMetadataDTO.noCapacityDays.map((t) => ({
                id: v.lineSubject,
                type: 'MESH' as const,
                name: 'No Capacity',
                range: {
                  startDate: t,
                  endDate: t,
                } as TRange,
                stackCount: 1 as const,
                color: '#ececec',
                mask: true,
              })),
              ...v.lineMetadataDTO.decreasedOperatorCountDays.map((t) => {
                const x = {
                  id: v.lineSubject,
                  type: 'CIRCLE' as const,
                  name: 'Decreased Operator Count',
                  range: {
                    startDate: t as TDate,
                    endDate: t as TDate,
                  },
                  stackCount: 1 as const,
                  color: '#FCBC69',
                  mask: false,
                };
                return x;
              }),
              ...v.lineMetadataDTO.increasedOperatorCountDays.map((t) => {
                const x = {
                  id: v.lineSubject,
                  type: 'CIRCLE' as const,
                  name: 'Increased Operator Count',
                  range: {
                    startDate: t as TDate,
                    endDate: t as TDate,
                  },
                  stackCount: 1 as const,
                  color: '#1FC295', //green
                  mask: false,
                };
                return x;
              }),
              // if increasedOperatorCountDays and increasedOperatorCountDaysAtFactoryLevel are both in same day, then the circle will be red [PLN-301]
              ...(v.lineMetadataDTO?.increasedOperatorCountDaysForLineAndFactory?.map((t) => {
                const x = {
                  id: v.lineSubject,
                  type: 'CIRCLE' as const,
                  name: 'Exceeding Default Operator Count',
                  range: {
                    startDate: t as TDate,
                    endDate: t as TDate,
                  },
                  stackCount: 1 as const,
                  color: '#ff6140', //red
                  mask: false,
                };
                return x;
              }) ?? []),
            ],
          };
        });

        // final return for transform function
        return {
          id: factoryInfo.factoryId,
          name: factoryInfo.llBoardLines[0]['leftPanel']['factoryName'],
          entities: [...machineAvailabilityLines, ...oplLines],
          data: factoryInfo,
          holidays: [
            ...factoryInfo.llBoardByFactoryMetadataDTO.holidays,
            ...factoryInfo.llBoardByFactoryMetadataDTO.nonWorkingDays,
          ] as TDate[],
          backgroundColor: getLeftNavBackgroundColour(factoryIndex, '#cfd0d4', '#f8f9f9'),
        };
      },
    );

    return timelineModel;
  },

  chageView: (type: TTimelineModel['boardView']['boardViewType'], timelineModel: TTimelineModel) => {
    return TIMELINE_MODEL.changeTimelineType(type, timelineModel);
  },

  applyLineDisableState: (timelineModel: TLLTimelineModel, validLineSubjects: string[]): TLLTimelineModel => {
    const machineIds = timelineModel.entities.flatMap((g) =>
      g.entities.filter((e) => e.id.includes(MACHINE_AVAILABILITY_LINE_ID_PREFIX)).map((e) => e.id),
    );
    return TIMELINE_MODEL.applyLineDisableState(timelineModel, [...validLineSubjects, ...machineIds]);
  },
};

// T1: Type for entity group data
// T2: Type for individual entity data
// T3: Type for element data

export type TLLRectDropElement = TRectDropElement<TLLElementData>;
export type TLLEntityActions = TEntityActions<TLLEntityGroupData, TLLEntityData, TLLElementData>;

export type TLLTimelineModel = TTimelineModel<TLLEntityGroupData, TLLEntityData, TLLElementData>;
